import { Injectable, NgZone } from '@angular/core';
import { ShellThemeInfo, ThemeType } from '@suiteux/suiteux-shell';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { InstrumentationService } from '../instrumentation/instrumentation.service';
import { FileItem } from '../models/file-item.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { AngularEvents } from '../shared-context/angular-events';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { UserContextService } from '../shared-context/user-context.service';
import { ConfigParserService } from './config-parser.service';
import { DocumentService } from './document.service';
import { EventService } from './event.service';
import { OfficeRouteService } from './office-route.service';
import { SchedulerService } from './scheduler.service';
import { ShellShimService } from './shell-shim.service';
import { StartingTheme, Theme, ThemeChangeEvent, ThemingService } from './theming.service';
import { WebPwaService } from './web-pwa.service';
import { WindowService } from './window.service';

@Injectable()
export class OfficeHomeThemingService extends ThemingService {
    constructor(
        documentService: DocumentService,
        eventService: EventService,
        capabilitiesService: CapabilitiesService,
        configParserService: ConfigParserService,
        clientStartupService: ClientStartupConfigService,
        contextService: ContextService,
        instrumentationService: InstrumentationService,
        userContext: UserContextService,
        routeService: OfficeRouteService,
        schedulerService: SchedulerService,
        locService: LocService,
        windowService: WindowService,
        private readonly ngZone: NgZone,
        shellShimService: ShellShimService,
        webPwaService: WebPwaService
    ) {
        super(
            documentService,
            eventService,
            capabilitiesService,
            configParserService,
            clientStartupService,
            contextService,
            instrumentationService,
            userContext,
            routeService,
            schedulerService,
            locService,
            windowService,
            shellShimService,
            webPwaService
        );
        // Call function to init the theming variables
        this.init();
    }

    protected createThemeChangeObservable(): Observable<ThemeChangeEvent> {
        return Observable.create(observer => {
            this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.themeChanged && event.args && event.args.length === 2 && event.args[0]))
                .subscribe(event => {
                    const args = event.args;
                    const themeData = args[0] as ShellThemeInfo;
                    const capabilities = this.capabilitiesService.capabilities;
                    if (themeData.IsDarkMode && themeData.ThemeId === 'base' && (!themeData.PreferOfficeTheme || !capabilities.fluentHeader)) {
                        themeData.Primary = themeData.Accent;
                    }
                    const shades = [
                        themeData.Darker || themeData.Primary,
                        themeData.Dark || themeData.Primary,
                        themeData.DarkAlt || themeData.Primary,
                        themeData.Primary || themeData.Primary,
                        themeData.Secondary || themeData.Primary,
                        themeData.Tertiary || themeData.Primary,
                        themeData.Light || themeData.Primary,
                        themeData.Lighter || themeData.Primary,
                        themeData.LighterAlt || themeData.Primary
                    ];

                    const showDarkMode =  this.showDarkMode();

                    this.ngZone.run(() => observer.next({
                        theme: {
                            shades,
                            reactShellTheme: themeData,
                            themeType: themeData.ThemeType,
                            themeId: themeData.ThemeId,
                            grayscaleThemeShades: showDarkMode ? ThemingService.grayscaleThemeShadesDark : ThemingService.grayscaleThemeShades
                        },
                        userSelected: args[1]
                    }));
                });
        });
    }

    protected showDarkMode(): boolean {
        return this.capabilitiesService.capabilities.darkModeEnabled
            && !!(this.userTheme && this.userTheme.reactShellTheme && this.userTheme.reactShellTheme.IsDarkMode)
            && this.darkModeSupported();
    }

    protected showContrastMode(): boolean {
        return false;
    }

    protected getInitialUserTheme(): Theme {
        const config = this.themeConfig;

        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            if (this.capabilitiesService.capabilities.isPwaInUwp) {
                const colorSettings = new Windows.UI.ViewManagement.UISettings();
                const colorValue = colorSettings.getColorValue(Windows.UI.ViewManagement.UIColorType.background);
                if (colorValue.a === 255 && colorValue.b === 0 && colorValue.g === 0 && colorValue.r === 0) {
                    return this.getDefaultDarkTheme();
                }
            } else if (config.isDarkMode || this.capabilitiesService.capabilities.darkModeOverride) {
                return this.getDefaultDarkTheme();
            }
        }
        if (config.preferOfficeTheme) {
            const theme = this.defaultTheme;
            const shades = this.getThemeShades(theme);
            return { shades, reactShellTheme: theme, themeType: 'App' as ThemeType, themeId: this.userContext.user.themeId };
        } else {
            const shades = ((this.themeConfig && this.themeConfig.userThemeShades) || []).map(shade => `#${shade}`);
            const reactShellTheme = { ...this.defaultTheme, ...this.getAppBrandTheme(shades) };
            const themeType = (this.themeConfig.useTenantTheme ? 'Tenant' : 'User') as ThemeType;
            return { shades, reactShellTheme, themeType, themeId: this.userContext.user.themeId };
        }

    }

    protected getStartingTheme(): StartingTheme {
        const appPageType = this.themeConfig.appPageTypeOnLoad;

        if (appPageType) {
            const app = FileItem.fileTypeNameToFileType(appPageType);
            if (this.supportedAppThemes.has(app)) {
                const supportedAppTheme = this.supportedAppThemes.get(app);
                const theme = this.showDarkMode() ? supportedAppTheme.darkTheme || supportedAppTheme.theme : supportedAppTheme.theme;
                if (!!theme) {
                    const shades = this.getThemeShades(theme);
                    const reactTheme = { ...this.defaultTheme, ...this.getAppBrandTheme(shades) } as ShellThemeInfo;
                    const startingTheme = { shades, reactShellTheme: reactTheme, themeType: 'App' as ThemeType, themeId: this.userContext.user.themeId };
                    return { theme: startingTheme, userSelected: false, appPage: app };
                }
            }
        }

        return { theme: this.userTheme, userSelected: true };
    }
}
